/**
 * Hook que renueva automáticamente la PAR de audio antes de que expire.
 *
 * Solo actúa cuando el audio viene del bucket (audioSrcAtom !== null).
 * Cuando el audio es un File local subido en esta sesión (audioFileAtom),
 * no hay expiración y este hook no hace nada.
 *
 * Uso: llamar desde cualquier pantalla que use el <audio> (Identify, Review).
 *
 *   function IdentifyScreen() {
 *     useAudioSrcRefresh();
 *     // ...
 *   }
 *
 * La PAR dura 10 min (definida en processings-files.ts). Este hook verifica
 * cada 30s y regenera si quedan menos de 1 minuto.
 *
 * El nombre del archivo (audio.wav o audio.mp4) viene de audioFilenameAtom,
 * seteado al cargar el meeting (Fase 22).
 */

import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { audioFilenameAtom, audioSrcAtom, currentProcessingIdAtom } from "../../state/atoms";
import { getProcessingFile } from "../../platform/api";

const REFRESH_THRESHOLD_MS = 60_000; // regenerar si quedan < 1 minuto
const CHECK_INTERVAL_MS = 30_000;    // verificar cada 30 segundos

export function useAudioSrcRefresh() {
  const audioSrc = useAtomValue(audioSrcAtom);
  const processingId = useAtomValue(currentProcessingIdAtom);
  const audioFilename = useAtomValue(audioFilenameAtom);
  const setAudioSrc = useSetAtom(audioSrcAtom);

  useEffect(() => {
    // Solo aplica cuando el audio viene del bucket.
    if (!audioSrc || !processingId) return;

    const refresh = async () => {
      // Re-leer expiresAt directo del closure capturado en este efecto.
      // Si la PAR fue renovada (nuevo expiresAt), el efecto se re-ejecuta
      // con un closure fresco — el intervalo anterior se cancela.
      const remaining = audioSrc.expiresAt - Date.now();
      if (remaining > REFRESH_THRESHOLD_MS) return;

      try {
        const info = await getProcessingFile(processingId, audioFilename);
        setAudioSrc({
          url: info.downloadUrl,
          expiresAt: new Date(info.expiresAt).getTime(),
        });
        console.log(`[useAudioSrcRefresh] PAR de ${audioFilename} renovada.`);
      } catch (err) {
        // No es fatal — el audio dejará de reproducirse al expirar, pero
        // el usuario puede seguir navegando. Se reintentará en el próximo check.
        console.warn("[useAudioSrcRefresh] No se pudo renovar PAR de audio:", err);
      }
    };

    void refresh(); // check inmediato al montar (por si la PAR ya casi expiró)

    const intervalId = setInterval(() => {
      void refresh();
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);

    // Cuando audioSrc.expiresAt cambia (PAR renovada), el efecto se re-ejecuta
    // con los valores actualizados y el intervalo anterior se limpia.
  }, [audioSrc?.expiresAt, processingId, audioFilename, setAudioSrc]);
}
